import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { Badge, Button, Card, EmptyState, Field, Input, Modal, Select, Spinner, Textarea, Pagination } from '../../components/ui';
import { useNotification } from '../../contexts/NotificationContext';
import { contactService } from '../../services/api';

const STATUS_TONE = { new: 'blue', read: 'yellow', resolved: 'green' };
const PAGE_SIZE = 20;

function AdminContactMessages() {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const load = () => {
    setLoading(true);
    contactService.getMessages({
      status: filter === 'all' ? undefined : filter,
      search: search || undefined,
      page,
      page_size: PAGE_SIZE,
    })
      .then((response) => {
        setMessages(response.data.results || response.data || []);
        setCount(Array.isArray(response.data) ? response.data.length : response.data.count || 0);
      })
      .catch(() => addNotification(t('Failed to load contact messages'), 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [filter, search, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const openMessage = async (message) => {
    let current = message;
    if (message.status === 'new') {
      try {
        const response = await contactService.updateMessage(message.id, { status: 'read' });
        current = response.data;
        setMessages((items) => items.map((item) => (item.id === message.id ? current : item)));
      } catch (_) {
        // The message can still be read even if the status update fails.
      }
    }
    setSelected(current);
    setNotes(current.admin_notes || '');
  };

  const updateStatus = async (status) => {
    if (!selected) return;
    setSaving(true);
    try {
      const response = await contactService.updateMessage(selected.id, { status, admin_notes: notes });
      setMessages((items) => items.map((item) => (item.id === selected.id ? response.data : item)));
      setSelected(response.data);
      addNotification(t('Contact message updated'), 'success');
      if (status === 'resolved') setSelected(null);
    } catch (_) {
      addNotification(t('Failed to update contact message'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <TopNav title={t('Contact messages')} subtitle={t('Messages submitted by visitors from the public contact form')} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row">
            <Input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder={t('Search name, email, subject, or message')} className="sm:max-w-md" />
            <Select value={filter} onChange={(event) => { setFilter(event.target.value); setPage(1); }} className="sm:w-44">
              <option value="all">{t('All messages')}</option>
              <option value="new">{t('New')}</option>
              <option value="read">{t('Read')}</option>
              <option value="resolved">{t('Resolved')}</option>
            </Select>
          </div>

          {loading ? (
            <div className="flex justify-center p-12"><Spinner /></div>
          ) : messages.length === 0 ? (
            <Card><EmptyState title={t('No contact messages found')} /></Card>
          ) : (
            <div className="space-y-3">
              {messages.map((message) => (
                <button key={message.id} type="button" onClick={() => openMessage(message)} className="block w-full text-start">
                  <Card className={`transition hover:-translate-y-0.5 hover:shadow-card-hover ${message.status === 'new' ? 'border-primary-200 bg-primary-50/30' : ''}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className={`truncate ${message.status === 'new' ? 'font-bold' : 'font-semibold'} text-gray-900`}>{message.subject}</h2>
                          <Badge tone={STATUS_TONE[message.status]}>{t(message.status === 'new' ? 'New' : message.status === 'read' ? 'Read' : 'Resolved')}</Badge>
                        </div>
                        <p className="mt-1 text-sm text-gray-700">{message.name} · {message.email}</p>
                        <p className="mt-2 line-clamp-2 text-sm text-gray-500">{message.message}</p>
                      </div>
                      <time className="shrink-0 text-xs text-gray-400">{new Date(message.created_at).toLocaleString()}</time>
                    </div>
                  </Card>
                </button>
              ))}
            </div>
          )}
          <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} className="mt-4 rounded-xl border bg-white" />
        </main>
      </div>

      <Modal open={Boolean(selected)} onClose={() => setSelected(null)} title={selected?.subject || ''} footer={selected && <>
        <Button variant="secondary" onClick={() => setSelected(null)}>{t('Close')}</Button>
        <Button variant="secondary" loading={saving} onClick={() => updateStatus('read')}>{t('Save notes')}</Button>
        <Button loading={saving} onClick={() => updateStatus('resolved')}>{t('Mark resolved')}</Button>
      </>}>
        {selected && (
          <div className="space-y-5">
            <div className="rounded-2xl bg-surface-100 p-4 text-sm">
              <p><span className="font-semibold">{t('From')}:</span> {selected.name}</p>
              <p className="mt-1"><span className="font-semibold">{t('Email')}:</span> <a className="text-primary-700 hover:underline" href={`mailto:${selected.email}`}>{selected.email}</a></p>
              {selected.phone && <p className="mt-1"><span className="font-semibold">{t('Phone')}:</span> <a className="text-primary-700 hover:underline" href={`tel:${selected.phone}`}>{selected.phone}</a></p>}
              <p className="mt-1 text-gray-500">{new Date(selected.created_at).toLocaleString()}</p>
            </div>
            <p className="whitespace-pre-wrap leading-7 text-gray-700">{selected.message}</p>
            <a href={`mailto:${selected.email}?subject=${encodeURIComponent(`Re: ${selected.subject}`)}`} className="inline-flex text-sm font-semibold text-primary-700 hover:underline">{t('Reply by email')}</a>
            <Field label={t('Internal admin notes')} hint={t('These notes are only visible to administrators.')}>
              <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={4} maxLength={2000} />
            </Field>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default AdminContactMessages;
